// ---------------------------------------------------------------------------
// Free Patent Cost Calculator — deterministic fee data + pure math.
//
// ZERO AI, ZERO network. Every figure here is a real, published number shown to
// the user with its source, per the brand's "sourced or dropped" discipline.
//
// USPTO amounts are the EXACT fees from the USPTO fee schedule effective
// January 19, 2025 (the FY2025 fee-setting rule). Small = 40% of the
// undiscounted rate, micro = 20% — verified internally consistent across every
// line. Attorney/agent figures are MARKET ESTIMATES (legal fees only, no USPTO
// fees), bracketed to the AIPLA Report of the Economic Survey (2023 report,
// 2022 data): provisional drafting median ~$5,000 (Q1 $3,000 / Q3 $7,300);
// utility drafting ~$8,000 minimal to ~$12,000 for complex technology areas
// (Q3 up to ~$15,000). They are directional, never a quote, and are always
// labeled as estimates — never presented as exact USPTO figures.
//
// When the USPTO next adjusts fees, edit FEE_EFFECTIVE and the FEES table
// together and nothing else drifts (the footnote + hero credential read the
// same constant).
// ---------------------------------------------------------------------------

export type Entity = 'micro' | 'small' | 'large';
export type FilingPath = 'prov' | 'nonprov';
export type Complexity = 'simple' | 'moderate' | 'complex';

export const FEE_EFFECTIVE = 'January 19, 2025';
export const USPTO_FEE_SCHEDULE_URL =
  'https://www.uspto.gov/learning-and-resources/fees-and-payment/uspto-fee-schedule';

interface EntityFees {
  prov: number;
  basic: number;
  search: number;
  exam: number;
  indep: number; // per independent claim over 3
  total: number; // per total claim over 20
  issue: number;
  maint35: number;
  maint75: number;
  maint115: number;
}

export const FEES: Record<Entity, EntityFees> = {
  large: { prov: 325, basic: 350, search: 770, exam: 880, indep: 600, total: 200, issue: 1290, maint35: 2150, maint75: 4040, maint115: 8280 },
  small: { prov: 130, basic: 140, search: 308, exam: 352, indep: 240, total: 80, issue: 516, maint35: 860, maint75: 1616, maint115: 3312 },
  micro: { prov: 65, basic: 70, search: 154, exam: 176, indep: 120, total: 40, issue: 258, maint35: 430, maint75: 808, maint115: 1656 },
};

// Late-payment surcharge for paying a maintenance fee during its 6-month grace
// period (37 CFR 1.20(h)), by entity — same USPTO schedule and FEE_EFFECTIVE
// date as the FEES table above (small = 40% of large, micro = 20%). Single-
// sourced here so the maintenance-fee calculator reads the same figures and
// nothing drifts when the USPTO next adjusts fees. Verified against the USPTO
// fee schedule effective January 19, 2025.
export const MAINT_SURCHARGE: Record<Entity, number> = { large: 540, small: 216, micro: 108 };

// USPTO fee codes per entity, for the ledger's "37 CFR … · fee ####" reference.
export const CODES = {
  prov: { large: 1005, small: 2005, micro: 3005 },
  basic: { large: 1011, small: 2011, micro: 3011 },
  search: { large: 1111, small: 2111, micro: 3111 },
  exam: { large: 1311, small: 2311, micro: 3311 },
  indep: { large: 1201, small: 2201, micro: 3201 },
  total: { large: 1202, small: 2202, micro: 3202 },
  issue: { large: 1501, small: 2501, micro: 3501 },
} as const;

// Attorney/agent professional fees — MARKET ESTIMATES, see the header note.
export const ATTORNEY: Record<FilingPath, Record<Complexity, [number, number]>> = {
  prov: { simple: [1500, 3000], moderate: [3000, 5500], complex: [5000, 8000] },
  nonprov: { simple: [6000, 9000], moderate: [8000, 13000], complex: [11000, 16000] },
};

export const ENTITY_LABEL: Record<Entity, string> = {
  micro: 'Micro entity',
  small: 'Small entity',
  large: 'Large entity',
};

// Discount vs. the undiscounted (large-entity) rate.
export const ENTITY_DISCOUNT: Record<Entity, string> = {
  micro: '80% off',
  small: '60% off',
  large: 'Full',
};

export const ENTITY_HELP: Record<Entity, string> = {
  micro: 'For independent inventors under an income cap with few prior filings.',
  small: 'Businesses under 500 employees, plus most startups and solo inventors.',
  large: 'Everyone else, including larger companies.',
};

export function money(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export interface LedgerRow {
  name: string;
  ref: string;
  code: number;
  amount: number;
}

export interface Ledger {
  rows: LedgerRow[];
  subtotal: number;
  attorney: [number, number];
  allIn: [number, number];
  caption: string;
  allInLabel: string;
  /** e.g. "Small entity · 60% off applied", or null for large entity. */
  discount: string | null;
  isProvisional: boolean;
  /** Excess-claim helper line for the claims control (non-provisional only). */
  claimsHelp: string;
}

export interface CalcState {
  entity: Entity;
  path: FilingPath;
  total: number;
  indep: number;
  complexity: Complexity;
  issue: boolean;
}

export const DEFAULT_STATE: CalcState = {
  entity: 'small',
  path: 'nonprov',
  total: 20,
  indep: 3,
  complexity: 'moderate',
  issue: true,
};

/** Pure, deterministic — the whole calculator. No AI, no network. */
export function computeLedger(s: CalcState): Ledger {
  const f = FEES[s.entity];
  const isProv = s.path === 'prov';
  const rows: LedgerRow[] = [];
  let subtotal = 0;
  let claimsHelp = '';

  if (isProv) {
    rows.push({ name: 'Provisional filing fee', ref: '1.16(d)', code: CODES.prov[s.entity], amount: f.prov });
    subtotal = f.prov;
  } else {
    rows.push({ name: 'Basic filing fee', ref: '1.16(a)', code: CODES.basic[s.entity], amount: f.basic });
    rows.push({ name: 'Search fee', ref: '1.16(k)', code: CODES.search[s.entity], amount: f.search });
    rows.push({ name: 'Examination fee', ref: '1.16(o)', code: CODES.exam[s.entity], amount: f.exam });
    subtotal = f.basic + f.search + f.exam;

    const excessIndep = Math.max(0, s.indep - 3);
    if (excessIndep > 0) {
      const amt = excessIndep * f.indep;
      rows.push({
        name: `Independent claims over 3 (${excessIndep} \u00d7 ${money(f.indep)})`,
        ref: '1.16(h)',
        code: CODES.indep[s.entity],
        amount: amt,
      });
      subtotal += amt;
    }
    const excessTotal = Math.max(0, s.total - 20);
    if (excessTotal > 0) {
      const amt = excessTotal * f.total;
      rows.push({
        name: `Claims over 20 total (${excessTotal} \u00d7 ${money(f.total)})`,
        ref: '1.16(i)',
        code: CODES.total[s.entity],
        amount: amt,
      });
      subtotal += amt;
    }
    if (s.issue) {
      rows.push({ name: 'Issue fee', ref: '1.18(a)', code: CODES.issue[s.entity], amount: f.issue });
      subtotal += f.issue;
    }

    const ip = excessIndep > 0
      ? `${excessIndep} independent over the 3 included`
      : 'Within the 3 independent claims included';
    const tp = excessTotal > 0 ? `${excessTotal} over the 20-claim total` : 'within 20 total';
    claimsHelp = `${ip} \u00b7 ${tp}`;
  }

  const attorney = ATTORNEY[s.path][s.complexity];
  const caption = isProv
    ? `Provisional \u00b7 ${ENTITY_LABEL[s.entity]}`
    : `Non-provisional utility \u00b7 ${ENTITY_LABEL[s.entity]} \u00b7 ${s.total} claim${s.total === 1 ? '' : 's'}`;
  const allInLabel = isProv
    ? 'Estimated all-in to file \u00b7 holds your date 12 months'
    : s.issue
      ? 'Estimated all-in, filing through grant'
      : 'Estimated all-in to file';
  const discount = s.entity === 'large' ? null : `${ENTITY_LABEL[s.entity]} \u00b7 ${ENTITY_DISCOUNT[s.entity]} applied`;

  return {
    rows,
    subtotal,
    attorney,
    allIn: [subtotal + attorney[0], subtotal + attorney[1]],
    caption,
    allInLabel,
    discount,
    isProvisional: isProv,
    claimsHelp,
  };
}
